import React, { MutableRefObject, ReactElement, useContext, useMemo, useRef, useState } from 'react'
import useIsMountedRef from 'use-is-mounted-ref'
import {
    DeepMap,
    FieldError,
    FieldValues,
    FormProvider,
    useForm,
    UseFormMethods
} from '../../../Vendor/react-hook-form/dist/index.ie11'
import { LanguageErrorCodes, NotificationsLanguageModal } from '../../../../i18n'
import { currentUserSettings } from '../../../NonReact/constants/ApiConstants'
import { HIDE } from '../../../NonReact/constants/class-names'
import { EMPTY_STRING } from '../../../NonReact/constants/Common'
import { RESTMethods } from '../../../NonReact/constants/REST'
import CLanguageHandler from '../../../NonReact/Languages/LanguageHandler'
import { ArrayUtils } from '../../../NonReact/utils/ArrayUtils'
import { RESTAPIError } from '../../Common/API'
import { ALL_VALUE } from '../../Common/API/ItemsGetters'
import { StatusCodes } from '../../../NonReact/common/API/REST/RESTAPIClient'
import { requestJSON } from '../../../NonReact/common/API/REST/RESTClient'
import { IconName } from '../../Common/Components/Icon'
import Tabs from '../../Common/Components/Tabs'
import { Tab } from '../../Common/Components/Tabs/Types'
import { ON_CHANGE } from '../../Common/Constants/HookFormEventNames'
import { LanguageContext, MessageContext, ReactState } from '../../Common/Contexts'
import {
    DEVICE_ALL,
    excludedNotifications,
    notificationChannels,
    notificationChannelsMapToNum,
    notificationChannelsMapToString,
    notificationOrder,
    NotificationsModalID,
    UserNotificationTypes
} from './Constants'
import FooterActions from './FooterActions'
import { LoadingNotificationSkelton } from './LoadingNotifications'
import { stateMapper } from './stateMapper'
import { ApplicationNotificationFormData, NotificationOrder } from './Types'
import Notifications from './views/Notifications'
import { UserNotificationsComponent } from './views/UserNotifications'
import { SelectOption } from '../../Common/Components/Selects/Types'
import {
    ApplicationResponseNotification,
    JSONResponse,
    UserNotificationsType
} from '../../../NonReact/common/Types/Types'

/**
 * Component properties type.
 */
type Props = {
    closeModal: () => void
    NotificationSettings: ApplicationResponseNotification
    refreshNotifications: (updatedNotifications: ApplicationResponseNotification) => void
}

/**
 * Configuration tabs container component.
 *
 * @param closeModal Modal close callback.
 * @param NotificationSettings Notification settings.
 * @param refreshNotifications Refresh notifications callback.
 */
const NotificationsTabs: React.FC<Props> = ({
    NotificationSettings,
    closeModal,
    refreshNotifications
}: Props): ReactElement => {
    const language = useContext(LanguageContext) as CLanguageHandler
    language.loadBundle(NotificationsLanguageModal.NAME)

    const [isSubmitting, setIsSubmitting]: ReactState<boolean> = useState<boolean>(false)

    const [serverError, setServerError]: ReactState<Definable<string>> = useState<string>()
    const [isValidationPopperOpen, setIsValidationPopperOpen]: ReactState<boolean> = useState<boolean>(false)

    const { displayError } = useContext(MessageContext)

    const isMounted: MutableRefObject<boolean> = useIsMountedRef()

    // Active tab ID.
    const [activeTabID, setActiveTabID]: ReactState<number> = useState<number>(NotificationsModalID.NOTIFICATIONS)

    const mapChannelStringToInts = (channels: string[]): number[] => {
        if (channels.length === 0) return [ALL_VALUE]

        const allowedChannels: string[] = notificationChannels.map(
            (notification: SelectOption<number>) => notificationChannelsMapToString[notification.value]
        )

        return channels
            .filter((channel: string): boolean => allowedChannels.includes(channel))
            .map((channel: string): number => notificationChannelsMapToNum[channel])
    }

    const reorderedUserNotifications = (userNotifications: UserNotificationsType[]): UserNotificationsType[] => {
        const filtereUserNotificationOnOrder: UserNotificationsType[] = userNotifications.filter(
            (userNotification: UserNotificationsType): boolean =>
                notificationOrder.some((order: NotificationOrder): boolean => userNotification.Name === order.name)
        )
        const unFilteredUserNotification: UserNotificationsType[] = userNotifications.filter(
            (userNotification: UserNotificationsType): boolean =>
                !notificationOrder.some((order: NotificationOrder): boolean => userNotification.Name === order.name)
        )

        const newNotifications: UserNotificationsType[] = notificationOrder.map(
            (order: NotificationOrder): UserNotificationsType => {
                const notifictionItem: Definable<UserNotificationsType> = filtereUserNotificationOnOrder.find(
                    (userNotification: UserNotificationsType): boolean => userNotification.Name === order.name
                )

                return {
                    Name: order.name,
                    Threshold: order.modified
                        ? order.modifyFunction(notifictionItem?.Threshold ?? 0)
                        : notifictionItem?.Threshold ?? 0,
                    NotificationEnabled: notifictionItem?.NotificationEnabled ?? false
                }
            }
        )

        return [...newNotifications, ...unFilteredUserNotification].filter(
            (notificiations: UserNotificationsType): boolean => !excludedNotifications.includes(notificiations.Name)
        )
    }

    const orderedUserNotifications: UserNotificationsType[] = useMemo(
        (): UserNotificationsType[] => reorderedUserNotifications(NotificationSettings.UserNotifications),
        [JSON.stringify(NotificationSettings.UserNotifications)]
    )

    const unwantedChannels: string[] = useMemo(() => {
        const allowedChannels: string[] = notificationChannels.map(
            (notification: SelectOption<number>) => notificationChannelsMapToString[notification.value]
        )

        return NotificationSettings.NotificationChannels.filter(
            (channel: string): boolean => !allowedChannels.includes(channel)
        )
    }, [JSON.stringify(NotificationSettings.NotificationChannels)])

    const defaultValues: MutableRefObject<ApplicationNotificationFormData> = useRef({
        settings: {
            enabled: !NotificationSettings.DoNotDisturb,
            telSysID: NotificationSettings.NotificationsTelSysID ?? ALL_VALUE,
            partitionIDInTelSys: NotificationSettings.NotificationsPartitionIDInTelSys ?? [0],
            deviceNumber: NotificationSettings.NotificationsDeviceNumber ?? DEVICE_ALL,
            telephoneNumber: NotificationSettings.TelephoneNumber ?? EMPTY_STRING,
            channels: mapChannelStringToInts(NotificationSettings.NotificationChannels) ?? [ALL_VALUE]
        },
        preferences: {
            ...ArrayUtils.createObjectFromArray<string>(
                orderedUserNotifications.map((user: UserNotificationsType, index: number): [string, string] => [
                    `${UserNotificationTypes.THRESHOLD}${index}`,
                    `${user.Threshold}`
                ])
            ),
            ...ArrayUtils.createObjectFromArray<boolean>(
                orderedUserNotifications.map((user: UserNotificationsType, index: number): [string, boolean] => [
                    `${UserNotificationTypes.STATUS}${index}`,
                    user.NotificationEnabled
                ])
            )
        }
    })

    // Form context.
    const methods: UseFormMethods = useForm({
        defaultValues: defaultValues.current,
        mode: ON_CHANGE,
        reValidateMode: ON_CHANGE,
        shouldUnregister: false
    })

    // eslint-disable-next-line
    const {
        handleSubmit,
        errors,
        formState: { isValid }
    }: UseFormMethods = methods

    /**
     * Tab activation method.
     * @param newActiveTabID ID of tab to activate.
     */
    const activateTab = (newActiveTabID: number): void => {
        setActiveTabID(newActiveTabID)
    }

    const determineValidationErrors = (formErrors: DeepMap<FieldValues, FieldError>): boolean[] => [
        !!formErrors?.settings,
        !!formErrors?.preferences
    ]

    const validationErrors: boolean[] = determineValidationErrors(errors)

    // eslint-disable-next-line
    const submitNotifications = async (state: ApplicationNotificationFormData): Promise<void> => {
        setIsSubmitting(true)

        const successfulSubmit = (request?: JSONResponse<ApplicationResponseNotification>): void => {
            if (request) refreshNotifications(request)
            closeModal()
        }

        const failedSubmit = (error: RESTAPIError): void => {
            const { errorCode, errorMessage, HTTPStatus }: RESTAPIError = error

            if (HTTPStatus === StatusCodes.OK || HTTPStatus === StatusCodes.OK_NO_CONTENT) {
                displayError(error)
                return successfulSubmit()
            }

            const errorCodeLanguage: Nullable<string> = (LanguageErrorCodes as unknown as Record<string, string>)[
                `Errorcode${errorCode}`
            ]

            setServerError(errorCodeLanguage ? language.get(errorCodeLanguage) : errorMessage)
            setIsSubmitting(false)

            return undefined
        }

        requestJSON<ApplicationResponseNotification>(currentUserSettings, {
            method: RESTMethods.PUT,
            body: stateMapper(state, NotificationSettings, orderedUserNotifications, unwantedChannels)
        })
            .then((request: JSONResponse<ApplicationResponseNotification>): void => successfulSubmit(request))
            .catch(failedSubmit)
    }

    // eslint-disable-next-line
    const invalidFormSubmit = (errors: DeepMap<FieldValues, FieldError>): void => {
        if (!isValid) {
            setActiveTabID(
                determineValidationErrors(errors).findIndex((validationError: boolean): boolean => validationError)
            )
            setIsValidationPopperOpen(true)

            setTimeout((): void => {
                if (isMounted.current) {
                    setIsValidationPopperOpen(false)
                }
            }, 2500)
        }
    }

    // List of all tabs settings.

    const tabs: Nullable<Tab[]> = [
        {
            ID: NotificationsModalID.NOTIFICATIONS,
            isDisplayed: true,
            tabsMenuItem: {
                title: language.get(NotificationsLanguageModal.TabSettingsTitle),
                iconName: IconName.SETTINGS,
                onClick: (): void => activateTab(NotificationsModalID.NOTIFICATIONS)
            },
            content: <Notifications NotificationSettings={NotificationSettings} />,
            hasError: validationErrors[NotificationsModalID.NOTIFICATIONS]
        },
        {
            ID: NotificationsModalID.UserNotifications,
            isDisplayed: true,
            tabsMenuItem: {
                title: language.get(NotificationsLanguageModal.TabPreferencesTitle),
                iconName: IconName.NOTIFICATION_ENABLED,
                onClick: (): void => activateTab(NotificationsModalID.UserNotifications)
            },
            content: <UserNotificationsComponent UserNotifications={orderedUserNotifications} />,
            hasError: validationErrors[NotificationsModalID.UserNotifications]
        }
    ]

    return (
        <div className='configuration-tabs'>
            {isSubmitting ? <LoadingNotificationSkelton /> : <></>}
            <FormProvider {...methods}>
                <form
                    // eslint-disable-next-line @typescript-eslint/no-misused-promises
                    onSubmit={handleSubmit(submitNotifications, invalidFormSubmit)}
                    className={isSubmitting ? HIDE : EMPTY_STRING}
                    noValidate
                >
                    {tabs && <Tabs tabs={tabs} activeTabID={activeTabID} />}
                    <FooterActions
                        serverError={serverError}
                        isValidationPopperOpen={isValidationPopperOpen}
                        closeModal={closeModal}
                    />
                </form>
            </FormProvider>
        </div>
    )
}

export default NotificationsTabs
